import {
  MaintenanceLogWithRelations,
  MaintenanceTaskWithSteps,
} from '@markku/types'
import {
  getCategoryColor,
  getCategoryLabel,
  groupBy,
  sortBy,
  sumBy,
} from '@markku/utils'
import dayjs from 'dayjs'

// Types

export interface CostItem {
  id: string
  type: 'task' | 'log'
  date: string | null
  name: string
  description: string | null
  category: string
  budget: number | null
  actual: number | null
  taskId: number | null
  logId?: number
}

export interface CategoryData {
  category: string
  cost: number
  color: string
}

export interface MonthlyData {
  month: string
  actual: number
  budget: number
}

export interface CostItemModalProps {
  task?: MaintenanceTaskWithSteps
  log?: MaintenanceLogWithRelations
}

// Calculation functions

export function calculateTotalCost(logs: MaintenanceLogWithRelations[]) {
  return sumBy(logs, (l) => parseFloat(l.actualCost || '0'))
}

export function calculateTotalBudget(tasks: MaintenanceTaskWithSteps[]) {
  return sumBy(
    tasks.filter((t) => t.deadline && t.estimatedCost),
    (t) => parseFloat(t.estimatedCost || '0'),
  )
}

export function calculateMonthlyData(
  logs: MaintenanceLogWithRelations[],
  tasks: MaintenanceTaskWithSteps[],
): MonthlyData[] {
  const logsByMonth = groupBy(logs, (l) => dayjs(l.completedDate).month())
  const tasksByMonth = groupBy(
    tasks.filter((t) => t.deadline && t.estimatedCost),
    (t) => dayjs(t.deadline).month(),
  )

  return Array.from({ length: 12 }, (_, i) => {
    const monthLogs = logsByMonth.get(i) || []
    const monthTasks = tasksByMonth.get(i) || []

    return {
      month: dayjs().month(i).format('MMM'),
      actual: sumBy(monthLogs, (l) => parseFloat(l.actualCost || '0')),
      budget: sumBy(monthTasks, (t) => parseFloat(t.estimatedCost || '0')),
    }
  })
}

export function calculateCategoryData(
  logs: MaintenanceLogWithRelations[],
): CategoryData[] {
  const logsByCategory = groupBy(logs, (log) => {
    return log.maintenanceTask?.category || 'other'
  })

  const categoryData = Array.from(logsByCategory.entries()).map(
    ([cat, items]) => ({
      category: getCategoryLabel(cat),
      cost: sumBy(items, (l) => parseFloat(l.actualCost || '0')),
      color: getCategoryColor(cat),
    }),
  )

  return sortBy(categoryData, [(c) => c.cost, 'desc'])
}

export function getCostItems(
  logs: MaintenanceLogWithRelations[],
  tasks: MaintenanceTaskWithSteps[],
  year: number,
): CostItem[] {
  const logItems: CostItem[] = logs.map((log) => ({
    id: `log-${log.id}`,
    type: 'log' as const,
    date: log.completedDate,
    name: log.taskNameSnapshot || '',
    description: log.descriptionSnapshot || log.notes,
    category: getCategoryLabel(log.maintenanceTask?.category || 'other'),
    budget: log.maintenanceTask?.estimatedCost
      ? parseFloat(log.maintenanceTask.estimatedCost)
      : null,
    actual: parseFloat(log.actualCost || '0'),
    taskId: log.maintenanceTaskId,
    logId: log.id,
  }))

  const incompleteTasks = tasks
    .filter((task) => {
      if (!task.deadline || !task.estimatedCost) return false
      const taskYear = dayjs(task.deadline).year()
      if (taskYear !== year) return false
      const hasLog = logs.some((log) => log.maintenanceTaskId === task.id)
      return !hasLog
    })
    .map(
      (task): CostItem => ({
        id: `task-${task.id}`,
        type: 'task' as const,
        date: task.deadline,
        name: task.name,
        description: task.description,
        category: getCategoryLabel(task.category || 'other'),
        budget: parseFloat(task.estimatedCost || '0'),
        actual: null,
        taskId: task.id,
      }),
    )

  return sortBy([...logItems, ...incompleteTasks], (item) => item.date)
}

export function getCostItemModalProps(
  item: CostItem,
  tasks: MaintenanceTaskWithSteps[],
  logs: MaintenanceLogWithRelations[],
): CostItemModalProps {
  const task = item.taskId ? tasks.find((t) => t.id === item.taskId) : undefined
  const log = item.logId ? logs.find((l) => l.id === item.logId) : undefined

  return { task, log }
}
